<template>
  <DesktopOnlyNotice v-if="!isDesktopPointer" title="Open provenance review on a desktop"
    message="Reviewing and correcting detections needs a mouse and a larger screen. Please open this on a desktop or laptop." />
  <div v-else class="review-page">
    <div class="review-inner">
      <div class="top-bar">
        <BackButton />
        <div class="top-actions">
          <!-- Same action as the scan pipeline manager's "Mark scan complete": release the scan
               (processing-complete) so the whole review + release flow can be done here. Shown once
               a review request exists for this scan and it isn't already resolved. -->
          <button v-if="canMarkComplete" class="success-btn" :disabled="markingComplete || loading" @click="markScanComplete">
            <v-progress-circular v-if="markingComplete" indeterminate size="16" width="2" color="#071514" />
            <template v-else><v-icon size="18" start>mdi-flag-checkered</v-icon>Mark scan complete</template>
          </button>
          <button class="primary-btn" :disabled="exporting || loading" @click="doExport">
            <v-progress-circular v-if="exporting" indeterminate size="16" width="2" color="#071514" />
            <template v-else><v-icon size="18" start>mdi-download-outline</v-icon>Export dataset (.zip)</template>
          </button>
        </div>
      </div>

      <section class="review-shell">
        <header class="review-header">
          <div>
            <div class="eyebrow">AI Inference</div>
            <h1 class="title">Provenance Review</h1>
          </div>
          <div class="stats">
            <span class="stat"><b>{{ stats.images }}</b> images</span>
            <span class="stat stat-nodet"><b>{{ stats.noDetection }}</b> no detections</span>
            <span class="stat"><b>{{ stats.total }}</b> detections</span>
            <span class="stat stat-tp"><b>{{ stats.tp }}</b> TP</span>
            <span class="stat stat-fp"><b>{{ stats.fp }}</b> FP</span>
            <span class="stat stat-unrev"><b>{{ stats.unreviewed }}</b> unreviewed</span>
            <span class="stat stat-fn"><b>{{ groundTruth.length }}</b> added</span>
          </div>
        </header>

        <div class="toolbar">
          <v-select v-model="provFilter" :items="provItems" item-title="title" item-value="value" label="Show"
            variant="outlined" density="compact" hide-details class="filter-vselect" />
          <v-select v-model="modeFilter" :items="modeItems" item-title="title" item-value="value" label="Mode"
            variant="outlined" density="compact" hide-details class="filter-vselect" />
          <v-select v-model="statusFilter" :items="statusItems" item-title="title" item-value="value" label="Status"
            variant="outlined" density="compact" hide-details class="filter-vselect" />
          <v-select v-model="classFilter" :items="classItems" item-title="title" item-value="value" label="Class"
            multiple chips closable-chips clearable variant="outlined" density="compact" hide-details
            class="filter-vselect filter-vselect-multi" />
        </div>

        <div v-if="loading" class="state">Loading inference images…</div>
        <div v-else-if="error" class="state error">Couldn't load provenance for this scan.</div>
        <div v-else-if="frames.length === 0" class="state">No images match the current filters.</div>

        <div v-else class="frame-gallery">
          <button v-for="frame in pagedFrames" :key="frame.stem" type="button" class="thumb" @click="openFrame(frame)">
            <div class="thumb-stage">
              <img :src="frame.url" :alt="frame.fileName" @load="onImgLoad(frame.stem, $event)" />
              <svg v-if="dims[frame.stem]" class="overlay ro"
                :viewBox="`0 0 ${dims[frame.stem].w} ${dims[frame.stem].h}`" preserveAspectRatio="none">
                <template v-for="d in frame.detections" :key="d.id">
                  <rect class="box-casing" :x="effectiveRect(d).x" :y="effectiveRect(d).y"
                    :width="effectiveRect(d).w" :height="effectiveRect(d).h" />
                  <rect class="box" :class="statusClass(d.reviewStatus)" :x="effectiveRect(d).x" :y="effectiveRect(d).y"
                    :width="effectiveRect(d).w" :height="effectiveRect(d).h" />
                </template>
                <template v-for="g in frame.groundTruths" :key="g.id">
                  <rect class="box-casing" :x="rectOf(g).x" :y="rectOf(g).y" :width="rectOf(g).w" :height="rectOf(g).h" />
                  <rect class="box box-fn" :x="rectOf(g).x" :y="rectOf(g).y" :width="rectOf(g).w" :height="rectOf(g).h" />
                </template>
              </svg>
            </div>
            <div class="thumb-foot">
              <span class="thumb-name">{{ frame.fileName }}</span>
              <span class="thumb-counts">
                <span v-if="frame.hasProvenance && markerCount(frame)" class="made">
                  {{ markerCount(frame) }} marker{{ markerCount(frame) === 1 ? '' : 's' }}
                </span>
                <span v-if="frame.hasProvenance && noMarkerCount(frame)" class="no-marker">
                  {{ noMarkerCount(frame) }} no marker
                </span>
                <span v-if="!frame.hasProvenance" class="no-det">no detections</span>
                <span v-if="unreviewedCount(frame)" class="pending">{{ unreviewedCount(frame) }} to review</span>
                <span v-if="frame.groundTruths.length" class="added">{{ frame.groundTruths.length }} added</span>
              </span>
            </div>
          </button>
        </div>

        <div v-if="!loading && !error && frames.length" class="pager">
          <span class="pager-info">Showing {{ rangeStart }}–{{ rangeEnd }} of {{ frames.length }}</span>
          <div class="pager-controls">
            <button class="pager-btn" :disabled="page <= 1" @click="page = 1" title="First">«</button>
            <button class="pager-btn" :disabled="page <= 1" @click="page--" title="Previous">‹</button>
            <span class="pager-page">
              Page
              <input class="pager-input" type="number" min="1" :max="pageCount" :value="page"
                @change="goToPage($event.target.value)" @keyup.enter="goToPage($event.target.value)" />
              / {{ pageCount }}
            </span>
            <button class="pager-btn" :disabled="page >= pageCount" @click="page++" title="Next">›</button>
            <button class="pager-btn" :disabled="page >= pageCount" @click="page = pageCount" title="Last">»</button>
          </div>
          <v-select v-model="pageSize" :items="pageSizeItems" item-title="title" item-value="value" label="Per page"
            variant="outlined" density="compact" hide-details class="filter-vselect pager-size" />
        </div>
      </section>
    </div>

    <!-- Per-frame review dialog -->
    <v-dialog v-model="dialogOpen" max-width="1160" @update:modelValue="onDialogToggle">
      <div v-if="dialogFrame" class="dlg">
        <header class="dlg-head">
          <div class="dlg-title">{{ dialogFrame.fileName }}</div>
          <button v-if="zoomBox" class="ghost-btn" @click="resetView">
            <v-icon size="15">mdi-fit-to-page-outline</v-icon><span>Fit</span>
          </button>
          <button class="ghost-btn" :class="{ 'is-active': drawMode }" @click="toggleDraw">
            <v-icon size="15">mdi-vector-rectangle</v-icon>
            <span>{{ drawMode ? 'Drawing…' : 'Add box' }}</span>
          </button>
          <button class="icon-btn" title="Close" @click="dialogOpen = false"><v-icon size="20">mdi-close</v-icon></button>
        </header>

        <div class="dlg-body">
          <div class="dlg-stage" :class="{ drawing: drawMode }">
            <img v-if="!dims[dialogFrame.stem]" :src="dialogFrame.url" class="dlg-preload"
              @load="onImgLoad(dialogFrame.stem, $event)" />
            <svg v-if="dims[dialogFrame.stem]" class="stage-svg" :class="{ draw: drawMode }" :viewBox="viewBox"
              preserveAspectRatio="xMidYMid meet"
              @pointerdown="onStagePointerDown" @pointermove="onStagePointerMove" @pointerup="onStagePointerUp">
              <image :href="dialogFrame.url" :width="dims[dialogFrame.stem].w" :height="dims[dialogFrame.stem].h"
                @click="onBackgroundClick" />
              <template v-for="d in dialogFrame.detections" :key="d.id">
                <rect class="box-casing" :class="{ big: selectedId === d.id || hoverId === d.id }"
                  :x="currentRect(d).x" :y="currentRect(d).y" :width="currentRect(d).w" :height="currentRect(d).h" />
                <rect class="box"
                  :class="[statusClass(d.reviewStatus), { selected: selectedId === d.id, hover: hoverId === d.id, movable: selectedId === d.id && !drawMode }]"
                  :x="currentRect(d).x" :y="currentRect(d).y" :width="currentRect(d).w" :height="currentRect(d).h"
                  @click.stop="select(d)" @mouseenter="hoverId = d.id" @mouseleave="clearHover(d.id)"
                  @pointerdown="onBoxPointerDown(d, $event)" />
              </template>
              <template v-for="g in dialogFrame.groundTruths" :key="g.id">
                <rect class="box-casing" :x="rectOf(g).x" :y="rectOf(g).y" :width="rectOf(g).w" :height="rectOf(g).h" />
                <rect class="box box-fn" :x="rectOf(g).x" :y="rectOf(g).y" :width="rectOf(g).w" :height="rectOf(g).h" />
              </template>
              <rect v-if="drawBox" :x="drawBox.x" :y="drawBox.y" :width="drawBox.w" :height="drawBox.h"
                class="box box-draw" />
              <template v-if="editRect && selectedId && !drawMode">
                <rect v-for="h in handlePoints" :key="h.corner" class="handle"
                  :x="h.x - handleR" :y="h.y - handleR" :width="handleR * 2" :height="handleR * 2"
                  @pointerdown.stop="onHandlePointerDown(h.corner, $event)" />
              </template>
            </svg>
          </div>

          <aside class="dlg-list">
            <div class="list-title">Detections ({{ dialogFrame.detections.length }})</div>
            <div v-if="dialogFrame.detections.length === 0" class="list-empty">No model detections on this frame.</div>

            <template v-for="group in dialogDetectionGroups" :key="group.projectId || 'none'">
              <div class="model-subhead"><v-icon size="13">mdi-cube-scan</v-icon>{{ group.name }}</div>

              <div v-for="d in group.detections" :key="d.id" class="rec"
                :class="[statusClass(d.reviewStatus), { sel: selectedId === d.id, hover: hoverId === d.id }]"
                @click="select(d)" @mouseenter="hoverId = d.id" @mouseleave="clearHover(d.id)">
                <div class="rec-top">
                  <span class="rec-dot" :class="statusClass(d.reviewStatus)" />
                  <span class="rec-class">{{ d.correctedLabel || d.label }}</span>
                  <span v-if="d.correctedLabel" class="rec-relabelled" :title="`was ${d.label}`">relabelled</span>
                  <span class="rec-conf">{{ pct(d.confidence) }}</span>
                </div>
                <div class="rec-sub">
                  <span :class="statusTextClass(d.reviewStatus)">{{ statusLabel(d.reviewStatus) }}</span>
                  <span class="rec-mode">{{ d.mode || '—' }}</span>
                  <span class="rec-marker" :class="d.markerIndex != null ? 'made' : 'none'">
                    {{ d.markerIndex != null ? 'Marker' : 'No marker' }}
                  </span>
                </div>

                <div v-if="selectedId === d.id" class="rec-actions" @click.stop>
                <div class="verdict-btns">
                  <button class="verdict tp" :disabled="savingId === d.id" @click="askVerdict(d, 'TruePositive')">
                    <v-icon size="14">mdi-check</v-icon> True
                  </button>
                  <button class="verdict fp" :disabled="savingId === d.id" @click="askVerdict(d, 'FalsePositive')">
                    <v-icon size="14">mdi-close</v-icon> False
                  </button>
                  <button class="verdict cancel" @click="cancelSelect">Cancel</button>
                </div>
                <div class="relabel-row">
                  <v-select v-model="correctedDraft" :items="relabelOptions(d)" class="relabel-select"
                    :placeholder="`Corrected class (${d.label})`" variant="outlined" density="compact"
                    hide-details clearable
                    no-data-text="No classes configured for this project" />
                  <button class="ghost-btn" :disabled="savingId === d.id"
                    @click="review(d, d.reviewStatus || 'TruePositive', true)">Save</button>
                </div>

                <div class="box-edit">
                  <span class="box-hint">Wrong fit? Drag the box or its corners on the image.</span>
                  <div v-if="boxDirty(d)" class="box-edit-actions">
                    <button class="primary-btn small" :disabled="savingId === d.id" @click="saveBox(d)">Save box</button>
                    <button class="ghost-btn" @click="resetBox(d)">Reset</button>
                  </div>
                </div>
              </div>
            </div>
            </template>

            <template v-if="dialogFrame.groundTruths.length">
              <div class="list-title">Added boxes ({{ dialogFrame.groundTruths.length }})</div>
              <div class="fn-chips">
                <span v-for="g in dialogFrame.groundTruths" :key="g.id" class="fn-chip"
                  :title="projectName(g.projectId)">
                  {{ g.className }}
                  <button class="fn-del" title="Remove" @click="deleteFn(g)"><v-icon size="13">mdi-close</v-icon></button>
                </span>
              </div>
            </template>

            <div v-if="pendingFn" class="fn-form">
              <span class="fn-hint">New box</span>
              <v-select v-model="pendingFnProjectId" :items="scanProjectItems" item-title="title" item-value="value"
                label="Project / model" variant="outlined" density="compact" hide-details class="fn-field" />
              <v-combobox v-model="pendingFnClass" :items="pendingFnClassItems" label="Class"
                variant="outlined" density="compact" hide-details class="fn-field" />
              <div class="fn-form-actions">
                <button class="primary-btn small" :disabled="!canSaveFn || savingFn" @click="saveFn">Save</button>
                <button class="ghost-btn" @click="cancelFn">Cancel</button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </v-dialog>

    <!-- Verdict confirmation -->
    <v-dialog v-model="confirmOpen" max-width="380">
      <div class="confirm">
        <p class="confirm-text">
          Mark this detection as
          <b :class="pendingVerdict && pendingVerdict.status === 'TruePositive' ? 'txt-tp' : 'txt-fp'">
            {{ pendingVerdict && pendingVerdict.status === 'TruePositive' ? 'a true positive' : 'a false positive' }}</b>?
        </p>
        <div class="confirm-actions">
          <button class="ghost-btn" @click="confirmOpen = false">Cancel</button>
          <button class="primary-btn small" @click="confirmVerdict">Confirm</button>
        </div>
      </div>
    </v-dialog>

  </div>
</template>

<script>
import BackButton from '@/components/buttons/BackButton.vue';
import DesktopOnlyNotice from '@/components/DesktopOnlyNotice.vue';
import { useConfirm } from '@/composable/useConfirm';
import { useDeviceCapability } from '@/composable/useDeviceCapability';
import MarkerProvenanceService from '@/services/MarkerProvenanceService';
import MarkerGroundTruthService from '@/services/MarkerGroundTruthService';
import InferenceBatchService from '@/services/InferenceBatchService';
import AiProjectService from '@/services/AiProjectService';
import ScanReviewRequestService from '@/services/ScanReviewRequestService';
import DualloAdminService from '@/services/DualloAdminService';

export default {
  name: 'ProvenanceReviewView',
  components: { BackButton, DesktopOnlyNotice },
  setup() {
    const { isDesktopPointer } = useDeviceCapability();
    return { isDesktopPointer };
  },
  props: {
    scanId: { type: String, required: true },
  },
  data() {
    return {
      provenance: [],
      groundTruth: [],
      // The scan's review-queue record (org/asset/scan ids + status) — resolves the ids needed to
      // mark the scan complete from here, mirroring the pipeline manager's action.
      reviewRequest: null,
      markingComplete: false,
      allFrames: [],       // every inference image in the batch: { stem, fileName, url }
      // Projects that ran this scan: { id, name, roboflowProjectId, workflowId, roboflowWorkflowId, classes: [] }
      scanProjects: [],
      dims: {},            // stem -> { w, h }
      loading: true,
      error: false,
      exporting: false,

      provFilter: 'All',
      modeFilter: 'All',
      statusFilter: 'All',
      classFilter: [],     // multi-select; empty = all classes

      // Gallery pagination (client-side — the frame set is already in memory; this keeps
      // us from rendering hundreds of thumbnails/images at once).
      page: 1,
      pageSize: 24,

      // Filter dropdown options (title/value pairs for v-select).
      provItems: [
        { title: 'All images', value: 'All' },
        { title: 'Has detections', value: 'HasProvenance' },
        { title: 'No detections (add boxes)', value: 'NoProvenance' },
      ],
      modeItems: [
        { title: 'All', value: 'All' },
        { title: 'Training', value: 'Training' },
        { title: 'Live', value: 'Live' },
      ],
      statusItems: [
        { title: 'All', value: 'All' },
        { title: 'Unreviewed', value: 'Unreviewed' },
        { title: 'True positive', value: 'TruePositive' },
        { title: 'False positive', value: 'FalsePositive' },
      ],
      pageSizeItems: [
        { title: '24 / page', value: 24 },
        { title: '48 / page', value: 48 },
        { title: '96 / page', value: 96 },
      ],

      // Dialog
      dialogOpen: false,
      selectedFrameStem: null,

      // Review
      selectedId: null,
      hoverId: null,
      correctedDraft: '',
      savingId: null,
      zoomBox: null,       // viewBox string when zoomed into a detection

      // Box editing (move/resize the selected detection's bbox)
      editRect: null,      // { x, y, w, h } working copy for the selected detection
      editing: null,       // { mode: 'move'|'resize', corner, startPt, startRect }
      dragMoved: false,
      suppressBgClick: false,

      // Verdict confirm
      confirmOpen: false,
      pendingVerdict: null,

      // FN drawing (dialog only)
      drawMode: false,
      drawStart: null,
      drawBox: null,
      pendingFn: null,
      pendingFnProjectId: null,
      pendingFnClass: '',
      savingFn: false,
    };
  },
  computed: {
    canonicals() {
      return this.provenance.filter(p => !p.isDuplicate);
    },
    // Show "Mark scan complete" once we have the scan's review-queue record (which carries the org +
    // asset ids the release call needs) and it hasn't already been accepted/rejected.
    canMarkComplete() {
      const r = this.reviewRequest;
      if (!r) return false;
      const org = r.organizationId ?? r.OrganizationId;
      const asset = r.assetId ?? r.AssetId;
      const status = (r.status ?? r.Status ?? '').toLowerCase();
      return !!org && !!asset && status !== 'accepted' && status !== 'rejected';
    },
    classes() {
      return [...new Set(this.canonicals.map(p => p.label).filter(Boolean))].sort();
    },
    // Every class configured across the scan's projects (Off/Training/Live), for relabelling.
    configuredClasses() {
      const names = new Set();
      for (const p of this.scanProjects) for (const c of p.classes ?? []) if (c) names.add(c);
      return [...names];
    },
    // Classes offered when relabelling: configured classes plus any observed labels as a fallback.
    knownClasses() {
      return [...new Set([...this.configuredClasses, ...this.classes])]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    },
    // Class filter options — every observed label (multi-select; empty selection = all).
    classItems() {
      return this.classes.map(c => ({ title: c, value: c }));
    },
    // Add-box project picker: the projects that ran this scan, labelled with their workflow.
    scanProjectItems() {
      return this.scanProjects.map(p => ({
        title: p.workflowId ? `${p.name} · ${p.workflowId}` : p.name,
        value: p.id,
      }));
    },
    // Classes offered for a new box — scoped to the project chosen in the add-box form.
    pendingFnClassItems() {
      const project = this.scanProjects.find(p => p.id === this.pendingFnProjectId);
      return (project?.classes ?? []).slice().sort((a, b) => a.localeCompare(b));
    },
    canSaveFn() {
      return !!this.pendingFnProjectId && !!(this.pendingFnClass || '').trim();
    },
    // Detections in the open frame grouped by the project (model) that produced them.
    dialogDetectionGroups() {
      if (!this.dialogFrame) return [];
      const groups = new Map();
      for (const d of this.dialogFrame.detections) {
        const key = d.projectId || '';
        if (!groups.has(key)) groups.set(key, { projectId: d.projectId, name: this.projectName(d.projectId), detections: [] });
        groups.get(key).detections.push(d);
      }
      return [...groups.values()];
    },
    filtered() {
      return this.canonicals.filter(p =>
        (this.modeFilter === 'All' || p.mode === this.modeFilter) &&
        (this.statusFilter === 'All' || (p.reviewStatus || 'Unreviewed') === this.statusFilter) &&
        (this.classFilter.length === 0 || this.classFilter.includes(p.label)));
    },
    // Stems of every image that produced at least one (canonical) detection — i.e. "has provenance".
    // Computed from the raw records, independent of the mode/status/class filters, so toggling those
    // never changes whether a frame counts as having provenance.
    provenanceStems() {
      const set = new Set();
      for (const p of this.canonicals) {
        const stem = MarkerProvenanceService.stemOf(p.sourceImageFileName);
        if (stem) set.add(stem);
      }
      return set;
    },
    stats() {
      const s = {
        total: this.canonicals.length, tp: 0, fp: 0, unreviewed: 0,
        images: this.allFrames.length,
        noDetection: this.allFrames.filter(f => !this.provenanceStems.has(f.stem)).length,
      };
      for (const p of this.canonicals) {
        if (p.reviewStatus === 'TruePositive') s.tp++;
        else if (p.reviewStatus === 'FalsePositive') s.fp++;
        else s.unreviewed++;
      }
      return s;
    },
    frames() {
      const detByStem = new Map();
      for (const d of this.filtered) {
        const stem = MarkerProvenanceService.stemOf(d.sourceImageFileName);
        if (!stem) continue;
        (detByStem.get(stem) ?? detByStem.set(stem, []).get(stem)).push(d);
      }
      const gtByStem = new Map();
      for (const g of this.groundTruth) {
        const stem = MarkerProvenanceService.stemOf(g.sourceImageFileName);
        if (!stem) continue;
        (gtByStem.get(stem) ?? gtByStem.set(stem, []).get(stem)).push(g);
      }

      return this.allFrames
        .filter((f) => {
          const hasProv = this.provenanceStems.has(f.stem);
          // A frame "matches" the detection filters if it still has a visible detection or an
          // added box under them.
          const matches = (detByStem.get(f.stem)?.length ?? 0) > 0 || (gtByStem.get(f.stem)?.length ?? 0) > 0;
          if (this.provFilter === 'NoProvenance') return !hasProv;
          if (this.provFilter === 'HasProvenance') return hasProv && matches;
          // All: images with no provenance (the ones to annotate) + provenance images that
          // still match the active detection filters.
          return !hasProv || matches;
        })
        .map((f) => ({
          ...f,
          hasProvenance: this.provenanceStems.has(f.stem),
          detections: detByStem.get(f.stem) ?? [],
          groundTruths: gtByStem.get(f.stem) ?? [],
        }))
        .sort((a, b) => (b.detections.length - a.detections.length) || a.fileName.localeCompare(b.fileName));
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.frames.length / this.pageSize));
    },
    pagedFrames() {
      const start = (this.page - 1) * this.pageSize;
      return this.frames.slice(start, start + this.pageSize);
    },
    rangeStart() {
      return this.frames.length ? (this.page - 1) * this.pageSize + 1 : 0;
    },
    rangeEnd() {
      return Math.min(this.page * this.pageSize, this.frames.length);
    },
    dialogFrame() {
      // Look across the whole (filtered) set, not just the current page, so an open frame
      // stays resolvable if the page changes underneath it.
      return this.frames.find(f => f.stem === this.selectedFrameStem) || null;
    },
    viewBox() {
      if (this.zoomBox) return this.zoomBox;
      const d = this.dims[this.dialogFrame?.stem];
      return d ? `0 0 ${d.w} ${d.h}` : '0 0 1 1';
    },
    // Handle size kept ~constant on screen by scaling with the visible (viewBox) width.
    handleR() {
      const w = parseFloat(this.viewBox.split(' ')[2]) || 100;
      return w / 55;
    },
    handlePoints() {
      const r = this.editRect;
      if (!r) return [];
      return [
        { corner: 'nw', x: r.x, y: r.y },
        { corner: 'ne', x: r.x + r.w, y: r.y },
        { corner: 'sw', x: r.x, y: r.y + r.h },
        { corner: 'se', x: r.x + r.w, y: r.y + r.h },
      ];
    },
  },
  watch: {
    // Any filter change resets to the first page so the user isn't stranded on a page
    // that no longer exists.
    provFilter() { this.page = 1; },
    modeFilter() { this.page = 1; },
    statusFilter() { this.page = 1; },
    classFilter() { this.page = 1; },
    pageSize() { this.page = 1; },
    // Clamp if the filtered set shrinks below the current page.
    pageCount(count) { if (this.page > count) this.page = count; },
  },
  async mounted() {
    // Touch devices see the desktop-only notice — skip loading the review data.
    if (!this.isDesktopPointer) return;
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = false;
      try {
        this.provenance = await MarkerProvenanceService.getByScanId(this.scanId);
        this.groundTruth = await MarkerGroundTruthService.getByScanId(this.scanId);
        this.allFrames = await MarkerProvenanceService.getScanFrames(this.scanId);
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
      // Non-blocking: resolves the projects that ran this scan (names + per-project classes) for
      // the per-model detection grouping and the add-box project/class pickers.
      this.loadScanProjects();
      // Non-blocking: resolve the review-queue record for this scan (org/asset ids + status) so the
      // "Mark scan complete" action can release it from here.
      this.loadReviewRequest();
    },

    // Finds this scan's entry in the review queue (best-effort). Gives us the organization + asset
    // ids needed to mark the scan complete without leaving the provenance screen.
    async loadReviewRequest() {
      try {
        const requests = await ScanReviewRequestService.getReviewRequests();
        this.reviewRequest = (requests ?? []).find(
          r => (r.scanId ?? r.ScanId ?? r.rowKey ?? r.RowKey) === this.scanId
        ) ?? null;
      } catch {
        this.reviewRequest = null;
      }
    },

    // Release the scan (processing-complete) — the same action as the scan pipeline manager's
    // "Mark scan complete" — so the review + release can be done entirely on this screen.
    async markScanComplete() {
      const r = this.reviewRequest;
      if (!r || this.markingComplete) return;
      const org = r.organizationId ?? r.OrganizationId;
      const asset = r.assetId ?? r.AssetId;
      if (!org || !asset) return;

      const { confirm } = useConfirm();
      const confirmed = await confirm({
        title: 'Mark scan complete?',
        message: 'This releases the scan and its AI results for the customer.',
        confirmText: 'Mark complete',
        icon: 'mdi-check-decagram-outline',
      });
      if (!confirmed) return;

      this.markingComplete = true;
      try {
        await DualloAdminService.setScanProcessingAsComplete(org, asset, this.scanId);
        this.$router.back();
      } catch (error) {
        console.error('Error marking scan complete:', error);
      } finally {
        this.markingComplete = false;
      }
    },

    /**
     * Resolves the projects that ran this scan — id, display name, workflow, and the project's
     * configured classes — for per-model detection grouping and the add-box pickers. Project ids
     * come from the inference batch record (present even when a run found nothing), falling back to
     * any project referenced by existing provenance for older scans.
     */
    async loadScanProjects() {
      const byId = new Map();
      const seed = (id, roboflowProjectId, workflowId, roboflowWorkflowId) => {
        if (!id) return;
        const cur = byId.get(id) ?? { id, roboflowProjectId: null, workflowId: null, roboflowWorkflowId: null };
        cur.roboflowProjectId = cur.roboflowProjectId || roboflowProjectId || null;
        cur.workflowId = cur.workflowId || workflowId || null;
        cur.roboflowWorkflowId = cur.roboflowWorkflowId || roboflowWorkflowId || null;
        byId.set(id, cur);
      };

      try {
        const batches = await InferenceBatchService.getByScanId(this.scanId);
        for (const b of batches ?? []) seed(b.projectId, b.roboflowProjectId, b.workflowId, b.roboflowWorkflowId);
      } catch { /* old scans have no batch record — fall back to provenance */ }
      for (const p of this.provenance) seed(p.projectId, p.roboflowProjectId, p.workflowId, p.roboflowWorkflowId);
      if (!byId.size) { this.scanProjects = []; return; }

      let registry = [];
      try { registry = await AiProjectService.getProjects(); } catch { /* names are optional */ }
      const regById = new Map((registry ?? []).map(r => [r.id, r]));

      this.scanProjects = await Promise.all([...byId.values()].map(async (p) => {
        const reg = regById.get(p.id);
        let classes = [];
        try {
          const cls = await AiProjectService.getProjectClasses(p.id);
          classes = (cls ?? []).map(c => c.className).filter(Boolean);
        } catch { /* skip a project we can't read */ }
        const roboflowProjectId = p.roboflowProjectId || reg?.roboflowProjectId || null;
        const name = reg?.displayName || reg?.projectName
          || (roboflowProjectId ? roboflowProjectId.split('/').pop() : p.id);
        return {
          id: p.id,
          name,
          roboflowProjectId,
          workflowId: p.workflowId || reg?.baseWorkflowUrl || null,
          roboflowWorkflowId: p.roboflowWorkflowId || reg?.baseWorkflowId || null,
          classes,
        };
      }));
    },

    projectName(projectId) {
      const p = this.scanProjects.find(x => x.id === projectId);
      return p?.name || 'Unknown model';
    },
    goToPage(raw) {
      let n = Number.parseInt(raw, 10);
      if (Number.isNaN(n)) n = this.page;
      this.page = Math.min(this.pageCount, Math.max(1, n));
    },
    onImgLoad(stem, evt) {
      this.dims = { ...this.dims, [stem]: { w: evt.target.naturalWidth, h: evt.target.naturalHeight } };
    },
    rectOf(box) {
      return {
        x: box.bboxX - box.bboxWidth / 2,
        y: box.bboxY - box.bboxHeight / 2,
        w: box.bboxWidth,
        h: box.bboxHeight,
      };
    },
    pct(v) { return `${Math.round((Number(v) || 0) * 100)}%`; },
    statusClass(s) {
      if (s === 'TruePositive') return 'box-tp';
      if (s === 'FalsePositive') return 'box-fp';
      return 'box-unrev';
    },
    statusTextClass(s) {
      if (s === 'TruePositive') return 'txt-tp';
      if (s === 'FalsePositive') return 'txt-fp';
      return 'txt-unrev';
    },
    statusLabel(s) {
      if (s === 'TruePositive') return 'True positive';
      if (s === 'FalsePositive') return 'False positive';
      return 'Unreviewed';
    },
    unreviewedCount(frame) {
      return frame.detections.filter(d => (d.reviewStatus || 'Unreviewed') === 'Unreviewed').length;
    },
    // Relabel choices for one detection: its own project's configured classes,
    // falling back to every known class when the project has none (or the
    // detection predates project stamping).
    relabelOptions(d) {
      const project = this.scanProjects.find(p => p.id === d.projectId);
      const classes = (project?.classes ?? []).filter(Boolean);
      const options = classes.length ? [...new Set(classes)] : this.knownClasses;
      return options.slice().sort((a, b) => a.localeCompare(b));
    },

    // A detection produced a viewer marker only when the pipeline assigned it a
    // marker index (Live-class canonical); Training-mode records and duplicates
    // are provenance-only.
    markerCount(frame) {
      return frame.detections.filter(d => d.markerIndex != null).length;
    },
    noMarkerCount(frame) {
      return frame.detections.length - this.markerCount(frame);
    },
    clearHover(id) {
      if (this.hoverId === id) this.hoverId = null;
    },

    openFrame(frame) {
      this.selectedFrameStem = frame.stem;
      this.selectedId = null;
      this.hoverId = null;
      this.correctedDraft = '';
      this.zoomBox = null;
      this.drawMode = false;
      this.drawBox = null;
      this.pendingFn = null;
      this.pendingFnProjectId = this.scanProjects.length === 1 ? this.scanProjects[0].id : null;
      this.pendingFnClass = '';
      this.dialogOpen = true;
    },
    onDialogToggle(open) {
      if (!open) {
        this.selectedFrameStem = null;
        this.selectedId = null;
        this.hoverId = null;
        this.zoomBox = null;
        this.drawMode = false;
        this.drawBox = null;
        this.pendingFn = null;
        this.pendingFnProjectId = null;
        this.pendingFnClass = '';
      }
    },

    // --- Box geometry (original vs reviewer-corrected) ---
    effectiveRect(d) {
      if (d.correctedBboxWidth != null && d.correctedBboxHeight != null) {
        return {
          x: d.correctedBboxX - d.correctedBboxWidth / 2,
          y: d.correctedBboxY - d.correctedBboxHeight / 2,
          w: d.correctedBboxWidth,
          h: d.correctedBboxHeight,
        };
      }
      return this.rectOf(d);
    },
    currentRect(d) {
      return (this.selectedId === d.id && this.editRect) ? this.editRect : this.effectiveRect(d);
    },
    boxDirty(d) {
      if (!this.editRect) return false;
      const e = this.effectiveRect(d);
      return Math.abs(this.editRect.x - e.x) > 0.5 || Math.abs(this.editRect.y - e.y) > 0.5
        || Math.abs(this.editRect.w - e.w) > 0.5 || Math.abs(this.editRect.h - e.h) > 0.5;
    },

    // --- Selection + zoom ---
    select(d) {
      const same = this.selectedId === d.id;
      this.selectedId = d.id;
      this.correctedDraft = d.correctedLabel || '';
      // Don't reset the working box / view when re-clicking the same box (preserves unsaved edits).
      if (!same) {
        this.editRect = this.effectiveRect(d);
        this.zoomTo(d);
      }
    },
    zoomTo(d) {
      const dim = this.dims[this.dialogFrame?.stem];
      if (!dim) return;
      const r = this.effectiveRect(d);
      // Show a comfortable margin around the box: ~3x its size, min 20% of the image.
      const vw = Math.min(dim.w, Math.max(r.w * 3, dim.w * 0.2));
      const vh = Math.min(dim.h, Math.max(r.h * 3, dim.h * 0.2));
      const cx = r.x + r.w / 2;
      const cy = r.y + r.h / 2;
      const x = Math.min(Math.max(cx - vw / 2, 0), dim.w - vw);
      const y = Math.min(Math.max(cy - vh / 2, 0), dim.h - vh);
      this.zoomBox = `${x} ${y} ${vw} ${vh}`;
    },
    resetView() {
      this.zoomBox = null;
    },
    cancelSelect() {
      this.selectedId = null;
      this.correctedDraft = '';
      this.editRect = null;
      this.editing = null;
      this.resetView();
    },
    onBackgroundClick() {
      if (this.suppressBgClick) { this.suppressBgClick = false; return; }
      if (this.drawMode) return;
      this.cancelSelect();
    },

    // --- Box move / resize ---
    onStagePointerDown(evt) {
      if (this.drawMode) this.onDrawStart(evt);
    },
    onStagePointerMove(evt) {
      if (this.editing) this.onEditMove(evt);
      else if (this.drawMode) this.onDrawMove(evt);
    },
    onStagePointerUp() {
      if (this.editing) this.onEditUp();
      else if (this.drawMode) this.onDrawEnd();
    },
    onBoxPointerDown(d, evt) {
      // Only the already-selected box is draggable (first click selects, then drag to adjust).
      if (this.drawMode || this.selectedId !== d.id || !this.editRect) return;
      this.editing = { mode: 'move', startPt: this.svgPoint(evt), startRect: { ...this.editRect } };
      this.dragMoved = false;
    },
    onHandlePointerDown(corner, evt) {
      if (this.drawMode || !this.editRect) return;
      this.editing = { mode: 'resize', corner, startPt: this.svgPoint(evt), startRect: { ...this.editRect } };
      this.dragMoved = false;
    },
    onEditMove(evt) {
      if (!this.editing || !this.editRect) return;
      const p = this.svgPoint(evt);
      const dx = p.x - this.editing.startPt.x;
      const dy = p.y - this.editing.startPt.y;
      if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) this.dragMoved = true;
      const s = this.editing.startRect;
      const r = this.editing.mode === 'move'
        ? { x: s.x + dx, y: s.y + dy, w: s.w, h: s.h }
        : this.applyResize(s, this.editing.corner, dx, dy);
      this.editRect = this.clampRect(r);
    },
    onEditUp() {
      if (this.dragMoved) this.suppressBgClick = true;  // don't let the trailing click deselect
      this.editing = null;
      this.dragMoved = false;
    },
    applyResize(s, corner, dx, dy) {
      let { x, y, w, h } = s;
      if (corner.includes('e')) w = s.w + dx;
      if (corner.includes('s')) h = s.h + dy;
      if (corner.includes('w')) { x = s.x + dx; w = s.w - dx; }
      if (corner.includes('n')) { y = s.y + dy; h = s.h - dy; }
      if (w < 4) { if (corner.includes('w')) x = s.x + s.w - 4; w = 4; }
      if (h < 4) { if (corner.includes('n')) y = s.y + s.h - 4; h = 4; }
      return { x, y, w, h };
    },
    clampRect(r) {
      const dim = this.dims[this.dialogFrame?.stem];
      let { x, y, w, h } = r;
      if (!dim) return { x, y, w, h };
      w = Math.max(4, Math.min(w, dim.w));
      h = Math.max(4, Math.min(h, dim.h));
      x = Math.min(Math.max(x, 0), dim.w - w);
      y = Math.min(Math.max(y, 0), dim.h - h);
      return { x, y, w, h };
    },
    saveBox(d) {
      if (!this.editRect) return;
      const cb = {
        x: this.editRect.x + this.editRect.w / 2,
        y: this.editRect.y + this.editRect.h / 2,
        width: this.editRect.w,
        height: this.editRect.h,
      };
      this.review(d, d.reviewStatus || 'TruePositive', false, cb);
    },
    resetBox(d) {
      this.editRect = this.effectiveRect(d);
    },

    // --- Review verdict (with confirm) ---
    askVerdict(detection, status) {
      this.pendingVerdict = { id: detection.id, status };
      this.confirmOpen = true;
    },
    async confirmVerdict() {
      const pending = this.pendingVerdict;
      this.confirmOpen = false;
      if (!pending) return;
      const detection = this.provenance.find(p => p.id === pending.id);
      this.pendingVerdict = null;
      if (detection) await this.review(detection, pending.status);
    },
    async review(detection, status, withRelabel = false, correctedBbox = null) {
      if (!detection) return;
      this.savingId = detection.id;
      try {
        const updated = await MarkerProvenanceService.review(this.scanId, detection.id, {
          reviewStatus: status,
          correctedLabel: withRelabel ? ((this.correctedDraft || '').trim() || null) : (detection.correctedLabel || null),
          reviewNotes: detection.reviewNotes || null,
          correctedBbox,
        });
        if (updated) {
          const idx = this.provenance.findIndex(p => p.id === detection.id);
          if (idx !== -1) this.provenance.splice(idx, 1, updated);
          // Keep the working box in sync with what was saved so it's no longer "dirty".
          if (this.selectedId === updated.id) this.editRect = this.effectiveRect(updated);
        }
      } finally {
        this.savingId = null;
      }
    },

    // --- FN drawing (dialog) ---
    toggleDraw() {
      this.drawMode = !this.drawMode;
      this.drawBox = null;
      this.drawStart = null;
    },
    svgPoint(evt) {
      // The event may fire on a child shape (box rect, resize handle) — only
      // the root SVG has createSVGPoint and the screen CTM that maps clientX/Y
      // into viewBox coordinates (zoomed or not). Using the child directly
      // used to return (0,0) as the drag start, making boxes explode on drag.
      const el = evt.currentTarget;
      const svg = el?.ownerSVGElement || el;
      const ctm = svg?.getScreenCTM?.();
      if (!ctm || !svg.createSVGPoint) return { x: 0, y: 0 };
      const pt = svg.createSVGPoint();
      pt.x = evt.clientX;
      pt.y = evt.clientY;
      const u = pt.matrixTransform(ctm.inverse());
      return { x: u.x, y: u.y };
    },
    onDrawStart(evt) {
      if (!this.drawMode) return;
      this.drawStart = this.svgPoint(evt);
      this.drawBox = { x: this.drawStart.x, y: this.drawStart.y, w: 0, h: 0 };
    },
    onDrawMove(evt) {
      if (!this.drawMode || !this.drawStart) return;
      const p = this.svgPoint(evt);
      this.drawBox = {
        x: Math.min(p.x, this.drawStart.x),
        y: Math.min(p.y, this.drawStart.y),
        w: Math.abs(p.x - this.drawStart.x),
        h: Math.abs(p.y - this.drawStart.y),
      };
    },
    onDrawEnd() {
      if (!this.drawMode || !this.drawBox) return;
      const b = this.drawBox;
      this.drawStart = null;
      this.drawBox = null;
      if (b.w < 4 || b.h < 4) return;
      this.pendingFn = { stem: this.dialogFrame.stem, fileName: this.dialogFrame.fileName, x: b.x, y: b.y, w: b.w, h: b.h };
      // Default the project to the only one that ran (or the class filter's project, if unambiguous).
      this.pendingFnProjectId = this.scanProjects.length === 1 ? this.scanProjects[0].id : (this.pendingFnProjectId || null);
      this.pendingFnClass = this.classFilter.length === 1 ? this.classFilter[0] : '';
    },
    async saveFn() {
      const name = (this.pendingFnClass || '').trim();
      const project = this.scanProjects.find(p => p.id === this.pendingFnProjectId);
      if (!name || !project || !this.pendingFn) return;
      this.savingFn = true;
      try {
        await MarkerGroundTruthService.create({
          scanId: this.scanId,
          projectId: project.id,
          roboflowProjectId: project.roboflowProjectId,
          workflowId: project.workflowId,
          className: name,
          sourceImageFileName: this.pendingFn.fileName,
          bboxX: this.pendingFn.x + this.pendingFn.w / 2,
          bboxY: this.pendingFn.y + this.pendingFn.h / 2,
          bboxWidth: this.pendingFn.w,
          bboxHeight: this.pendingFn.h,
        });
        this.groundTruth = await MarkerGroundTruthService.getByScanId(this.scanId);
        this.pendingFn = null;
        this.pendingFnClass = '';
        this.drawMode = false;
      } finally {
        this.savingFn = false;
      }
    },
    cancelFn() {
      this.pendingFn = null;
      this.pendingFnClass = '';
      this.drawMode = false;
    },
    async deleteFn(g) {
      await MarkerGroundTruthService.delete(this.scanId, g.id);
      this.groundTruth = this.groundTruth.filter(x => x.id !== g.id);
    },

    /** Decode image bytes (e.g. webp) and re-encode as JPEG, returning an ArrayBuffer (or null). */
    async transcodeToJpeg(arrayBuffer) {
      try {
        const bitmap = await createImageBitmap(new Blob([arrayBuffer]));
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        bitmap.close?.();
        const jpeg = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92));
        return jpeg ? await jpeg.arrayBuffer() : null;
      } catch {
        return null;
      }
    },

    async doExport() {
      this.exporting = true;
      try {
        const options = this.classFilter.length ? { classNames: this.classFilter } : {};
        // Per-project export slices: [{ projectId, roboflowProjectId, projectSlug, coco }, ...].
        const slices = await MarkerProvenanceService.exportDataset(this.scanId, options);
        if (!Array.isArray(slices) || slices.length === 0) return;

        const byStem = {};
        for (const f of this.allFrames) byStem[f.stem] = f;

        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();

        // JPEG bytes per frame, cached by stem so an image shared across projects is prepared once.
        // Everything exports as .jpg: the real inference JPEG from blob when present, otherwise the
        // viewer webp transcoded to JPEG here — never webp in the zip.
        const jpegCache = new Map();
        const jpegFor = (frame) => {
          if (jpegCache.has(frame.stem)) return jpegCache.get(frame.stem);
          const p = (async () => {
            if (frame.inferenceUrl) {
              const jpg = await fetch(frame.inferenceUrl).then(r => (r.ok ? r.arrayBuffer() : null)).catch(() => null);
              if (jpg) return jpg;
            }
            if (frame.url) {
              const webp = await fetch(frame.url).then(r => (r.ok ? r.arrayBuffer() : null)).catch(() => null);
              if (webp) return this.transcodeToJpeg(webp);
            }
            return null;
          })();
          jpegCache.set(frame.stem, p);
          return p;
        };

        // Ensure folder names are unique even if two projects share a slug.
        const usedFolders = new Set();
        for (const slice of slices) {
          const coco = slice.coco || {};
          let folder = (slice.projectSlug || slice.projectId || 'project').replace(/[^A-Za-z0-9._-]/g, '_');
          while (usedFolders.has(folder)) folder = `${folder}_`;
          usedFolders.add(folder);

          const keptImageIds = new Set();
          for (const img of coco.images ?? []) {
            const frame = byStem[MarkerProvenanceService.stemOf(img.file_name)];
            if (!frame) continue;
            const bytes = await jpegFor(frame);
            if (!bytes) continue;
            const name = frame.inferenceFileName || `${frame.stem}.jpg`;
            zip.file(`${folder}/${name}`, bytes);
            img.file_name = name;
            keptImageIds.add(img.id);
          }

          coco.images = (coco.images ?? []).filter(i => keptImageIds.has(i.id));
          coco.annotations = (coco.annotations ?? []).filter(a => keptImageIds.has(a.image_id));
          zip.file(`${folder}/_annotations.coco.json`, JSON.stringify(coco, null, 2));
        }

        const blob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `retraining-coco-${this.scanId}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Fill the scroll container so the page itself doesn't scroll — only the gallery does. */
.review-page {
  display: flex;
  justify-content: center;
  height: 100%;
  min-height: 0;
}

/* Match the admin page's centred, responsive content width. */
.review-inner {
  width: 100%;
  height: 100%;
  min-height: 0;
  margin-inline: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
@media (min-width: 1920px) { .review-inner { max-width: 83.333%; } }
@media (min-width: 2560px) { .review-inner { max-width: 66.667%; } }

.top-bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex: 0 0 auto; }

.review-shell {
  flex: 1 1 auto;
  min-height: 0;
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 16px;
  padding: 18px 20px;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.review-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; flex: 0 0 auto; }
.eyebrow { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8aa0aa; }
.title { font-size: 1.4rem; font-weight: 700; margin: 2px 0 0; }

.stats { display: flex; gap: 14px; font-size: 0.82rem; color: #8aa0aa; flex-wrap: wrap; align-items: center; }
.stat b { color: #fff; }
.stat-tp b { color: var(--duallo-accent); }
.stat-fp b { color: #ff8a80; }
.stat-unrev b { color: #ffcc80; }
.stat-fn b { color: #82b1ff; }
.stat-nodet b { color: #b0bec5; }

.toolbar { display: flex; gap: 8px; flex-wrap: wrap; flex: 0 0 auto; }
/* Vuetify selects, matched to the AI admin screens' dropdowns. */
.filter-vselect { min-width: 168px; max-width: 230px; }
.filter-vselect :deep(.v-field) { border-radius: 10px; background: rgba(255, 255, 255, 0.03); font-size: 0.82rem; }
/* The class filter holds chips for each selected class — give it room to breathe. */
.filter-vselect-multi { min-width: 200px; max-width: 340px; }

.state { padding: 40px; text-align: center; color: #8aa0aa; flex: 1 1 auto; }
.state.error { color: #ff8a80; }

.frame-gallery {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 240px), 1fr));
  gap: 12px;
  padding-right: 4px;
  align-content: start;
}

.thumb {
  display: flex; flex-direction: column; padding: 0; text-align: left;
  background: rgba(0, 0, 0, 0.22); border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px; overflow: hidden; cursor: pointer; font: inherit;
  transition: border-color 0.15s ease, transform 0.05s ease;
}
.thumb:hover { border-color: rgba(100, 255, 218, 0.35); }
.thumb:active { transform: scale(0.995); }

.thumb-stage { position: relative; width: 100%; line-height: 0; background: #0d1418; }
.thumb-stage img { width: 100%; height: auto; display: block; }

.thumb-foot { display: flex; flex-direction: column; gap: 3px; padding: 8px 10px; }
.thumb-name {
  font-size: 0.76rem; color: #cfd8dc; font-family: 'JetBrains Mono', Consolas, monospace;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.thumb-counts { display: flex; gap: 8px; font-size: 0.72rem; color: #8aa0aa; flex-wrap: wrap; }
.thumb-counts .pending { color: #ffcc80; }
.thumb-counts .added { color: #82b1ff; }
.thumb-counts .no-det { color: #78909c; font-style: italic; }

/* Pager */
.pager {
  flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 12px;
  flex-wrap: wrap; padding-top: 12px; margin-top: 4px; border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.pager-info { font-size: 0.8rem; color: #8aa0aa; }
.pager-controls { display: flex; align-items: center; gap: 6px; }
.pager-page { font-size: 0.82rem; color: #cfd8dc; padding: 0 8px; white-space: nowrap; display: inline-flex; align-items: center; gap: 6px; }
.pager-input {
  width: 52px; padding: 5px 6px; border-radius: 7px; background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.14); color: #fff; font: inherit; font-size: 0.82rem; text-align: center;
  color-scheme: dark;
}
.pager-input:focus { outline: none; border-color: rgba(100, 255, 218, 0.4); }
.pager-btn {
  min-width: 34px; padding: 6px 10px; border-radius: 8px; background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1); color: #cfd8dc; font: inherit; font-size: 0.9rem; cursor: pointer;
}
.pager-btn:hover:not(:disabled) { background: rgba(100, 255, 218, 0.08); color: #fff; }
.pager-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.pager-size { min-width: 130px; max-width: 150px; }

/* Overlay boxes */
.overlay { position: absolute; inset: 0; width: 100%; height: 100%; }
.overlay.ro { pointer-events: none; }
/* Dark casing drawn under each colored line so boxes stay visible on any background. */
.box-casing { fill: transparent; stroke: rgba(0, 0, 0, 0.8); stroke-width: 4; vector-effect: non-scaling-stroke; pointer-events: none; }
.box-casing.big { stroke-width: 6; }
.box { fill: transparent; stroke-width: 2; vector-effect: non-scaling-stroke; }
.box-unrev { stroke: #ffcc80; }
.box-tp { stroke: #64ffda; }
.box-fp { stroke: #ff8a80; stroke-dasharray: 6 4; }
.box-fn { stroke: #82b1ff; fill: rgba(130, 177, 255, 0.12); }
.box-draw { stroke: #fff; stroke-dasharray: 4 3; fill: rgba(255, 255, 255, 0.08); }

/* ----- Dialog ----- */
.dlg {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; color: #fff;
  display: flex; flex-direction: column; max-height: 90vh; overflow: hidden;
}
.dlg-head {
  display: flex; align-items: center; gap: 10px; padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.dlg-title {
  font-weight: 600; font-size: 0.9rem; font-family: 'JetBrains Mono', Consolas, monospace;
  flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.icon-btn { background: transparent; border: none; color: #cfd8dc; cursor: pointer; display: inline-flex; padding: 4px; border-radius: 8px; }
.icon-btn:hover { background: rgba(255, 255, 255, 0.06); color: #fff; }

.dlg-body { display: flex; min-height: 0; height: 72vh; }

.dlg-stage {
  position: relative; flex: 1 1 auto; min-width: 0;
  background: #0d1418; padding: 18px; box-sizing: border-box;
}
.dlg-preload { display: none; }
.stage-svg { width: 100%; height: 100%; display: block; touch-action: none; }
.stage-svg.draw { cursor: crosshair; }
.stage-svg .box:not(.box-fn):not(.box-draw) { cursor: pointer; }
.stage-svg image { cursor: default; }
.box.selected { stroke-width: 4; }
.box.hover { stroke-width: 4; filter: drop-shadow(0 0 3px currentColor); }
.box.movable { cursor: move; }

/* Motion, dialog only, to draw the eye: the selected box gets marching ants, and
   still-unreviewed boxes gently pulse so the distribution of pending work stands out.
   Reviewed (TP/FP) boxes stay static so "done vs to-do" reads at a glance. */
@keyframes box-ants { to { stroke-dashoffset: -26; } }
@keyframes box-pulse { 0%, 100% { stroke-opacity: 1; } 50% { stroke-opacity: 0.4; } }
.stage-svg .box.selected { stroke-dasharray: 8 5; animation: box-ants 0.6s linear infinite; }
.stage-svg .box-unrev:not(.selected) { animation: box-pulse 1.5s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .stage-svg .box.selected, .stage-svg .box-unrev:not(.selected) { animation: none; }
}
.handle { fill: #64ffda; stroke: #071514; stroke-width: 1; vector-effect: non-scaling-stroke; cursor: nwse-resize; }
.handle:hover { fill: #9dffe9; }

.dlg-list {
  flex: 0 0 300px; display: flex; flex-direction: column; gap: 6px; padding: 12px;
  overflow-y: auto; border-left: 1px solid rgba(255, 255, 255, 0.06);
}
.list-title { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.06em; color: #8aa0aa; font-weight: 700; margin-top: 4px; }
.list-empty { font-size: 0.82rem; color: #8aa0aa; padding: 6px 0; }
.model-subhead {
  display: flex; align-items: center; gap: 5px; margin-top: 8px; padding: 3px 8px; border-radius: 6px;
  font-size: 0.72rem; font-weight: 700; color: #9fd8ff; background: rgba(130, 177, 255, 0.1);
  border: 1px solid rgba(130, 177, 255, 0.24);
}
.fn-field :deep(.v-field) { border-radius: 9px; background: rgba(255, 255, 255, 0.04); font-size: 0.82rem; }

.rec {
  border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 9px; padding: 8px 10px;
  cursor: pointer; background: rgba(255, 255, 255, 0.02); transition: border-color 0.12s ease, background 0.12s ease;
}
.rec:hover, .rec.hover { background: rgba(255, 255, 255, 0.06); }
.rec.sel { border-color: rgba(100, 255, 218, 0.4); background: rgba(100, 255, 218, 0.06); }
.rec-top { display: flex; align-items: center; gap: 6px; }
.rec-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
.rec-dot.box-unrev { background: #ffcc80; }
.rec-dot.box-tp { background: var(--duallo-accent); }
.rec-dot.box-fp { background: #ff8a80; }
.rec-class { font-weight: 600; font-size: 0.85rem; flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rec-relabelled { font-size: 0.62rem; color: #82b1ff; border: 1px solid rgba(130,177,255,0.35); border-radius: 5px; padding: 0 4px; }
.rec-conf { font-size: 0.78rem; color: #8aa0aa; }
.rec-sub { display: flex; justify-content: space-between; gap: 8px; font-size: 0.74rem; margin-top: 2px; }
.rec-mode { color: #8aa0aa; }

.rec-marker {
  margin-left: auto;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
}
.rec-marker.made {
  background: rgba(102, 199, 180, 0.16);
  border: 1px solid rgba(102, 199, 180, 0.45);
  color: var(--duallo-accent-muted);
}
.rec-marker.none {
  background: rgba(138, 160, 170, 0.1);
  border: 1px solid rgba(138, 160, 170, 0.3);
  color: #8aa0aa;
}

.thumb-counts .made { color: var(--duallo-accent-muted); }
.thumb-counts .no-marker { color: #8aa0aa; }
.txt-tp { color: var(--duallo-accent); }
.txt-fp { color: #ff8a80; }
.txt-unrev { color: #ffcc80; }

.rec-actions { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
.verdict-btns { display: flex; gap: 6px; }
.verdict {
  flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 4px;
  padding: 7px; border-radius: 8px; font: inherit; font-size: 0.76rem; font-weight: 600;
  cursor: pointer; border: 1px solid transparent;
}
.verdict.tp { background: rgba(100, 255, 218, 0.12); color: var(--duallo-accent); border-color: rgba(100, 255, 218, 0.3); }
.verdict.fp { background: rgba(255, 138, 128, 0.1); color: #ff8a80; border-color: rgba(255, 138, 128, 0.3); }
.verdict.cancel { background: rgba(255, 255, 255, 0.04); color: #cfd8dc; border-color: rgba(255, 255, 255, 0.12); flex: 0 0 auto; }
.verdict:disabled { opacity: 0.5; cursor: not-allowed; }

.relabel-row { display: flex; gap: 6px; align-items: center; }
.relabel-select { flex: 1; min-width: 0; font-size: 12.5px; }
.relabel-input {
  flex: 1 1 auto; min-width: 0; padding: 6px 8px; border-radius: 8px; background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1); color: #fff; font: inherit; font-size: 0.8rem;
  /* Darken the native datalist suggestion popup (known classes) in Chromium. */
  color-scheme: dark;
}
.relabel-input option { background: var(--duallo-card-start); color: #eceff1; }
.relabel-input:focus { outline: none; border-color: rgba(100, 255, 218, 0.4); }

.box-edit { display: flex; flex-direction: column; gap: 8px; border-top: 1px solid rgba(255, 255, 255, 0.06); padding-top: 8px; }
.box-hint { font-size: 0.72rem; color: #8aa0aa; line-height: 1.35; }
.box-edit-actions { display: flex; gap: 8px; }

.fn-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.fn-chip {
  display: inline-flex; align-items: center; gap: 4px; padding: 3px 4px 3px 10px; border-radius: 999px;
  background: rgba(130, 177, 255, 0.12); border: 1px solid rgba(130, 177, 255, 0.3); color: #82b1ff; font-size: 0.74rem;
}
.fn-del { background: transparent; border: none; color: inherit; cursor: pointer; display: inline-flex; }

.fn-form { display: flex; flex-direction: column; gap: 8px; border-top: 1px solid rgba(255, 255, 255, 0.06); padding-top: 10px; margin-top: 4px; }
.fn-hint { font-size: 0.72rem; color: #8aa0aa; text-transform: uppercase; letter-spacing: 0.04em; }
.fn-form-actions { display: flex; gap: 8px; }

/* ----- Confirm ----- */
.confirm {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 20px; color: #fff;
  display: flex; flex-direction: column; gap: 16px;
}
.confirm-text { margin: 0; font-size: 0.95rem; line-height: 1.5; }
.confirm-actions { display: flex; justify-content: flex-end; gap: 10px; }

.ghost-btn {
  display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 8px;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cfd8dc; font: inherit; font-size: 0.8rem; font-weight: 500; cursor: pointer;
}
.ghost-btn:hover:not(:disabled) { background: rgba(100, 255, 218, 0.06); color: #fff; }
.ghost-btn.is-active { background: rgba(100, 255, 218, 0.14); border-color: rgba(100, 255, 218, 0.4); color: var(--duallo-accent); }
.ghost-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.primary-btn {
  padding: 10px 18px; border-radius: 10px; background: var(--duallo-accent-gradient);
  color: #071514; font-weight: 700; border: none; font: inherit; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; min-width: 150px;
}
.primary-btn.small { min-width: 90px; padding: 8px 14px; }
.primary-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.top-actions { display: flex; align-items: center; gap: 10px; }
.success-btn {
  padding: 10px 18px; border-radius: 10px; background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
  color: #06210d; font-weight: 700; border: none; font: inherit; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; min-width: 170px;
}
.success-btn:disabled { opacity: 0.5; cursor: not-allowed; }

@media (max-width: 760px) {
  .dlg-body { flex-direction: column; height: auto; }
  .dlg-stage { height: 46vh; flex: 0 0 auto; }
  .dlg-list { flex: 1 1 auto; border-left: none; border-top: 1px solid rgba(255, 255, 255, 0.06); }
}
</style>
